import { KeyValue } from '@angular/common';
import { Component, HostBinding, Inject, OnDestroy, OnInit } from '@angular/core';
import { XlatPipe } from '@amt/i18n/xlat-pipe';
import { INextInspectionDateGroup } from '@Models/condition-monitoring/INextInspectionDateGroup';
import { ListBoxToolbarConfig } from '@progress/kendo-angular-listbox';
import { ConditionMonitoringService, IDisplayVehicle } from '../condition-monitoring.service';
import { getExpectedInspectionDate } from '@OC-common/utils/condition-monitoring-visual-inspections.utils';
import { IConditonMonitoringTabs } from '@Models/condition-monitoring/IConditionMonitoringTabs';
import { IRendererConfig } from './renderer-config-provider/IRendererConfig';

export const ICON_WIDTH = 96;
export const ICON_HEIGHT = 96;

export interface IGroup<T> {
    name: string,
    members: T[];
}

export type DropdownDisplay = { text: string, value: number };

@Component({
    selector: 'condition-monitoring-base',
    template: ``,
    styleUrls: ['./condition-monitoring-base.component.scss'],

 })
export class ConditionMonitoringBaseComponent implements OnInit, OnDestroy {
    private _title: string;
    isProcessing: boolean;
    statusText: string;
    public get ShowActiveInspectionsOnly() {
        let element = <HTMLInputElement>document.getElementById("chkDisplayExceptionsOnly");
        return element.checked;
    }

    //These are set in the child components
    protected _displayBy: DropdownDisplay[] = [];
    displayBySelectedItem: any;

    private _vehicleSpecs: Array<string> = [];
    private _specsToShow: Array<string> = [];

    readonly sortBy: DropdownDisplay[] = [
        { text: this.xlatPipe.transform("conditionmonitoring.none"), value: 1 },
        { text: this.xlatPipe.transform("conditionmonitoring.vehicle_application"), value: 2 },
        { text: this.xlatPipe.transform("conditionmonitoring.vehicle_specification"), value: 3 },
        { text: this.xlatPipe.transform("conditionmonitoring.next_inspection_date"), value: 0 },
    ];

    protected readonly nextInspectionDateGroups: INextInspectionDateGroup[] = [];
    protected readonly detailsTabs: IConditonMonitoringTabs[] = [];

    protected getNextInspectionGroups({ issues }: IDisplayVehicle): INextInspectionDateGroup {
        const selected = issues?.map((issue) => ({
            lastInspection: issue.inspectionDate,
            nextInspection: getExpectedInspectionDate(issue),
            issue
        })).sort((
            { lastInspection: aLast, nextInspection: aNext },
            { lastInspection: bLast, nextInspection: bNext },
        ) => aNext.getTime() - bNext.getTime() || bLast.getTime() - aLast.getTime())[0];
        const { lastInspection, nextInspection } = selected;
        const result = this.nextInspectionDateGroups.filter((group) => group.isInGroup(lastInspection, nextInspection));
        return result[0];
    }

    //Default item
    sortBySelectedItem: any = this.sortBy.find(elem => elem.value === 2);

    public setTitle(subTitle: string) {
        this._title = `${this.xlatPipe.transform("DashboardWidget.ConditionMonitoring")} - ${this.xlatPipe.transform(subTitle)}`;
    }

    public get VehicleSpecs() {
        return this._vehicleSpecs;
    }

    public get SpecsToShow() {
        return this._specsToShow;
    }

    public get DisplayBy() {
        return this._displayBy;
    }

    public get title() {
        return this._title;
    }

    public rendererConfig?: IRendererConfig;

    public toolbarSettings: ListBoxToolbarConfig = {
        tools: ["transferAllTo", "transferFrom", "transferTo", "transferAllFrom" ],
    };

    @HostBinding("style.--img-w") public readonly cssImgWidth = ICON_WIDTH;
    @HostBinding("style.--img-h") public readonly cssImgHeight = ICON_HEIGHT;

    constructor(protected monSvc: ConditionMonitoringService, protected xlatPipe: XlatPipe,
        @Inject('WindowFactory') protected WindowFactory: any) {
    }
    ngOnDestroy(): void { }
    ngOnInit(): void {
        this.setProcessing();

        //configure dropdowns
        this.configureDropdowns();
        //Should load localstorage here for the last saved values and set?
    }

    protected setProcessing() {
        this.statusText = this.xlatPipe.transform("conditionmonitoring.loading_vehicle_data");
        this.isProcessing = true;
    }

    private configureDropdowns() {
        this.configureVehicleSpecsDropdown();
    }

    public showFilters: boolean = false;

    public onShowFiltersClick() {
        if (this.showFilters) {
            this.onUpdateButtonClick();
        } else {
            this.toggleFilter();
        }
    }

    protected toggleFilter(updateClicked: boolean = false) {
        if (updateClicked && !this.showFilters)
            return;

        this.showFilters = !this.showFilters;
    }

    //Implemented in child components
    public onUpdateButtonClick(): void { }

    //Pass a 'new' date into this so we don't corupot the original date (passed by reference)
    protected addDays(date: Date, days: number): Date {
        date.setDate(date.getDate() + days);
        return date;
    }
        
    protected groupBy(vehicles: IDisplayVehicle[], groupBy: number) {
        let groupMap: Map<String, IDisplayVehicle[]>;
        let result: Array<IGroup<IDisplayVehicle>>;

        //Initial serialNo sort
        vehicles.sort(({ serialNo: a }, { serialNo: b }) => a.localeCompare(b));

        switch (groupBy) {
            case 0: {
                //Last Inspection Date
                groupMap = vehicles.filter(i => i.issues?.length)
                    .reduce((acc, v) => {
                        const group = this.getNextInspectionGroups(v);
                        if (!group) return acc;
                        return acc.set(group?.text, (acc.get(group?.text) || []).concat(v))
                    }, new Map<string, IDisplayVehicle[]>());
                result = this.nextInspectionDateGroups
                    .map(({ text }) => ({ name: text, members: groupMap.get(text)}))
                    .filter(({ members }) => !!members && members.length > 0);
                break;
            }

            case 1: {
                //None
                groupMap = vehicles.reduce(
                    (acc, v) => acc.set("Vehicles", (acc.get("Vehicles") || []).concat(v)),
                    new Map<string, IDisplayVehicle[]>());
                result = Array.from(groupMap, e => <IGroup<IDisplayVehicle>>({ name: e[0], members: e[1] }));
                break;
            }

            case 3: {
                //Vehicle Specification
                groupMap = vehicles.reduce(
                    (acc, v) => acc.set(v.model, (acc.get(v.model) || []).concat(v)),
                    new Map<string, IDisplayVehicle[]>());
                result = Array.from(groupMap, e => <IGroup<IDisplayVehicle>>({ name: e[0], members: e[1] })).sort(({ name: a }, { name: b }) => a == "Haul Truck" ? -1 : (b == "Haul Truck" ? 1 : a.localeCompare(b)));
                break;
            }

            case 2:
            default:
                //Vehicle application
                //from: https://stackoverflow.com/a/47752730
                groupMap = vehicles.reduce(
                    (acc, v) => acc.set(v.application, (acc.get(v.application) || []).concat(v)),
                    new Map<string, IDisplayVehicle[]>());
                result = Array.from(groupMap, e => <IGroup<IDisplayVehicle>>({ name: e[0], members: e[1] })).sort(({ name: a }, { name: b }) => a == "Haul Truck" ? -1 : (b == "Haul Truck" ? 1 : a.localeCompare(b)));
        }

        return result;
    }

    public trackByGroup(i: number, group: KeyValue<string, any>) {
        return group.key;
    }

    public trackByVehicle(i: number, veh: IDisplayVehicle) {
        return veh.serialNo;
    }

    public click(v: IDisplayVehicle) {
        const renderer = this.rendererConfig;
        this.WindowFactory.openItem({
            component: 'condition-monitoring-details',
            caption: this.xlatPipe.transform('conditionmonitoring.details_title', v.serialNo),
            windowRelatedRecordId: v.serialNo,
            initParams: {
                vehicle: v,
                rendererConfig: renderer,
                serialNumber: v.serialNo,
                tabs: this.detailsTabs
            },
            width: 800,
            height: 850,
            allowOverflow: true,
            isAngular: true
        });
    }

    private async configureVehicleSpecsDropdown(selected?: any) { //Change any to a dropdowntype interface?
        this.monSvc.VehicleSpecs$.subscribe(vehSpecs => {
            this._vehicleSpecs = Array.from(vehSpecs.values()).map(elem => (elem.model)).sort();
        });
    };
}